import type { OutletDetail } from '@/domain/entities/outlet.entity'

/**
 * Port dari `usecase.Calculate` di BE (readme-BE "Pajak, service & pembulatan").
 * Fungsi murni: tanpa jaringan, tanpa `Date.now()`, tanpa state. Masuk angka,
 * keluar angka.
 *
 * Alasannya bukan kerapian. BE adalah PENCATAT, bukan kalkulator: ia menyimpan
 * total kiriman frontend apa adanya dan hanya membandingkannya dengan hitungan
 * sendiri untuk menandai `totals_inconsistent`. Kalau rumus di sini meleset,
 * struk yang dipegang pelanggan berbeda dari catatan di server — dan tidak ada
 * yang bisa memperbaikinya setelah uangnya masuk laci.
 *
 * Semua uang integer rupiah. JANGAN pernah float: pembulatan biner menghasilkan
 * selisih rupiah yang tidak bisa dijelaskan di laporan bulanan.
 */

/** Ke terdekat, titik tengah ke ATAS. Sama dengan `roundHalfUp` di BE. */
export function roundHalfUp(value: number): number {
  return Math.floor(value + 0.5)
}

export interface PricedAddon {
  readonly price: number
  readonly qty: number
}

/** Satu baris keranjang, sudah lepas dari bentuk UI-nya. */
export interface PricedLine {
  /**
   * Sudah termasuk harga variant kalau ada — harga variant bersifat ABSOLUT,
   * menggantikan harga produk, bukan menambahnya.
   */
  readonly unitPrice: number
  readonly qty: number
  /** Harga addon bersifat DELTA: dikali qty-nya sendiri dulu, baru ikut qty baris. */
  readonly addons: readonly PricedAddon[]
  readonly discountAmount: number
  /** `void` dan `compliment` sama-sama tidak masuk subtotal, tapi beda arti. */
  readonly status: 'normal' | 'void' | 'compliment'
}

export interface LineAmounts {
  readonly gross: number
  readonly discountAmount: number
  readonly lineTotal: number
}

export interface TransactionDiscount {
  readonly type: string
  readonly value: number
  readonly amount?: number
}

export interface Totals {
  readonly subtotal: number
  readonly discountAmount: number
  readonly afterDiscount: number
  readonly complimentValue: number
  readonly serviceAmount: number
  readonly taxAmount: number
  readonly roundingAmount: number
  readonly grandTotal: number
}

/**
 * `line_gross = (unit_price + Σ addon.price × addon.qty) × qty`
 *
 * Baris void tetap punya gross: barisnya tidak pernah dihapus dan laporan
 * pembatalan membutuhkan angkanya.
 */
export function calculateLine(line: PricedLine): LineAmounts {
  const addonPerUnit = line.addons.reduce((sum, a) => sum + a.price * a.qty, 0)
  const gross = (line.unitPrice + addonPerUnit) * line.qty

  if (line.status !== 'normal') {
    // Void tidak ikut subtotal sama sekali; compliment bernilai 0 tapi
    // grossnya ditotal terpisah sebagai biaya promosi.
    return { gross, discountAmount: 0, lineTotal: 0 }
  }

  // Potongan tidak pernah boleh melebihi angka dasarnya sendiri — total
  // negatif berarti kasir membayar pelanggan.
  const discountAmount = Math.min(Math.max(line.discountAmount, 0), gross)
  return { gross, discountAmount, lineTotal: gross - discountAmount }
}

/** Potongan level transaksi, ditahan agar tidak melebihi subtotal. */
function transactionDiscountAmount(subtotal: number, discount: TransactionDiscount | null): number {
  if (!discount) return 0
  if (typeof discount.amount === 'number' && discount.amount >= 0) {
    return Math.min(discount.amount, subtotal)
  }
  if (discount.value <= 0) return 0
  const raw =
    discount.type === 'percent'
      ? roundHalfUp((subtotal * Math.min(discount.value, 100)) / 100)
      : discount.value
  return Math.min(Math.max(raw, 0), subtotal)
}

function applyRounding(grandTotal: number, outlet: OutletDetail): number {
  if (!outlet.roundingEnabled || outlet.roundingUnit <= 0) return 0
  const u = outlet.roundingUnit
  let rounded: number
  switch (outlet.roundingMode) {
    case 'down':
      rounded = Math.floor(grandTotal / u) * u
      break
    case 'up':
      rounded = Math.ceil(grandTotal / u) * u
      break
    default:
      rounded = Math.floor((grandTotal + u / 2) / u) * u
  }
  return rounded - grandTotal
}

/**
 * Urutan tetap, berlaku persis sama di kedua mode pajak:
 *
 * 1. `line_gross`, 2. `line_total`, 3. `subtotal`, 4. `after_discount`,
 * 5. service, pajak, lalu pembulatan.
 */
export function calculateTotals(
  lines: readonly PricedLine[],
  discount: TransactionDiscount | null,
  outlet: OutletDetail,
): Totals {
  let subtotal = 0
  let complimentValue = 0

  for (const line of lines) {
    const amounts = calculateLine(line)
    if (line.status === 'normal') subtotal += amounts.lineTotal
    else if (line.status === 'compliment') complimentValue += amounts.gross
  }

  const discountAmount = transactionDiscountAmount(subtotal, discount)
  const afterDiscount = subtotal - discountAmount

  let serviceAmount: number
  let taxAmount: number
  let grandTotal: number

  const taxType =
    outlet.taxType ??
    (outlet.taxInclusive ? 'inclusive_after_discount' : 'exclusive_after_discount')
  const serviceMode = outlet.serviceChargeMode ?? 'service_charge_is_taxed'

  switch (taxType) {
    case 'inclusive_after_discount': {
      switch (serviceMode) {
        case 'service_free_tax': {
          serviceAmount = roundHalfUp((afterDiscount * outlet.serviceValue) / 100)
          const taxNet = roundHalfUp((afterDiscount * 100) / (100 + outlet.taxValue))
          taxAmount = afterDiscount - taxNet
          break
        }
        case 'tax_is_service_charge': {
          serviceAmount = roundHalfUp((afterDiscount * outlet.serviceValue) / 100)
          const taxNet = roundHalfUp((afterDiscount * 100) / (100 + outlet.taxValue))
          taxAmount = afterDiscount - taxNet
          break
        }
        case 'service_charge_is_taxed_from_subtotal': {
          serviceAmount = roundHalfUp((subtotal * outlet.serviceValue) / 100)
          const grossWithService = afterDiscount + serviceAmount
          const taxNet = roundHalfUp((grossWithService * 100) / (100 + outlet.taxValue))
          taxAmount = grossWithService - taxNet
          break
        }
        default: {
          serviceAmount = roundHalfUp((afterDiscount * outlet.serviceValue) / 100)
          const grossWithService = afterDiscount + serviceAmount
          const taxNet = roundHalfUp((grossWithService * 100) / (100 + outlet.taxValue))
          taxAmount = grossWithService - taxNet
          break
        }
      }
      grandTotal = afterDiscount + serviceAmount
      break
    }
    case 'inclusive_before_discount': {
      switch (serviceMode) {
        case 'service_free_tax': {
          serviceAmount = roundHalfUp((afterDiscount * outlet.serviceValue) / 100)
          const subNet = roundHalfUp((subtotal * 100) / (100 + outlet.taxValue))
          taxAmount = subtotal - subNet
          break
        }
        case 'tax_is_service_charge': {
          const subNet = roundHalfUp((subtotal * 100) / (100 + outlet.taxValue))
          taxAmount = subtotal - subNet
          serviceAmount = roundHalfUp(((afterDiscount + taxAmount) * outlet.serviceValue) / 100)
          break
        }
        case 'service_charge_is_taxed_from_subtotal': {
          serviceAmount = roundHalfUp((subtotal * outlet.serviceValue) / 100)
          const grossSubWithService = subtotal + serviceAmount
          const subNet = roundHalfUp((grossSubWithService * 100) / (100 + outlet.taxValue))
          taxAmount = grossSubWithService - subNet
          break
        }
        default: {
          serviceAmount = roundHalfUp((afterDiscount * outlet.serviceValue) / 100)
          const grossSubWithService = subtotal + serviceAmount
          const subNet = roundHalfUp((grossSubWithService * 100) / (100 + outlet.taxValue))
          taxAmount = grossSubWithService - subNet
          break
        }
      }
      grandTotal = afterDiscount + serviceAmount
      break
    }
    case 'exclusive_before_discount': {
      const taxBase = subtotal
      const serviceBase =
        serviceMode === 'service_charge_is_taxed_from_subtotal' ? subtotal : afterDiscount

      switch (serviceMode) {
        case 'service_free_tax':
          serviceAmount = roundHalfUp((serviceBase * outlet.serviceValue) / 100)
          taxAmount = roundHalfUp((taxBase * outlet.taxValue) / 100)
          break
        case 'tax_is_service_charge':
          taxAmount = roundHalfUp((taxBase * outlet.taxValue) / 100)
          serviceAmount = roundHalfUp(((serviceBase + taxAmount) * outlet.serviceValue) / 100)
          break
        case 'service_charge_is_taxed_from_subtotal':
          serviceAmount = roundHalfUp((subtotal * outlet.serviceValue) / 100)
          taxAmount = roundHalfUp(((taxBase + serviceAmount) * outlet.taxValue) / 100)
          break
        default:
          serviceAmount = roundHalfUp((serviceBase * outlet.serviceValue) / 100)
          taxAmount = roundHalfUp(((taxBase + serviceAmount) * outlet.taxValue) / 100)
          break
      }
      grandTotal = afterDiscount + serviceAmount + taxAmount
      break
    }
    case 'exclusive_after_discount':
    default: {
      const taxBase = afterDiscount
      const serviceBase =
        serviceMode === 'service_charge_is_taxed_from_subtotal' ? subtotal : afterDiscount

      switch (serviceMode) {
        case 'service_free_tax':
          serviceAmount = roundHalfUp((serviceBase * outlet.serviceValue) / 100)
          taxAmount = roundHalfUp((taxBase * outlet.taxValue) / 100)
          break
        case 'tax_is_service_charge':
          taxAmount = roundHalfUp((taxBase * outlet.taxValue) / 100)
          serviceAmount = roundHalfUp(((serviceBase + taxAmount) * outlet.serviceValue) / 100)
          break
        case 'service_charge_is_taxed_from_subtotal':
          serviceAmount = roundHalfUp((subtotal * outlet.serviceValue) / 100)
          taxAmount = roundHalfUp(((taxBase + serviceAmount) * outlet.taxValue) / 100)
          break
        default:
          serviceAmount = roundHalfUp((serviceBase * outlet.serviceValue) / 100)
          taxAmount = roundHalfUp(((taxBase + serviceAmount) * outlet.taxValue) / 100)
          break
      }
      grandTotal = afterDiscount + serviceAmount + taxAmount
      break
    }
  }

  const roundingAmount = applyRounding(grandTotal, outlet)

  return {
    subtotal,
    discountAmount,
    afterDiscount,
    complimentValue,
    serviceAmount,
    taxAmount,
    roundingAmount,
    grandTotal: grandTotal + roundingAmount,
  }
}
